import { useId } from 'react';
import type { CalculatorField } from '@/content/financial/calculators';

/**
 * A single calculator input.
 *
 * Numeric fields keep their raw string while focused so intermediate states
 * ("1", "1.", "-") survive typing; the value is clamped and coerced to a number
 * on blur. The parent therefore sees a string mid-edit and a number once the
 * field settles, which is why `compute` reads everything through `num()`.
 */
export default function FieldInput({
  field,
  value,
  onChange,
}: {
  field: CalculatorField;
  value: number | string;
  onChange: (value: number | string) => void;
}) {
  const id = useId();
  const describedBy = field.help ? `${id}-help` : undefined;

  const labelClass = 'mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-200';

  if (field.kind === 'select') {
    return (
      <div>
        <label htmlFor={id} className={labelClass}>
          {field.label}
        </label>
        <select
          id={id}
          value={String(value)}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cheong-500 dark:border-surface-dark-border dark:bg-surface-dark-card dark:text-ink-50"
        >
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {field.help && <FieldHelp id={describedBy!}>{field.help}</FieldHelp>}
      </div>
    );
  }

  const prefix = field.kind === 'currency' ? '$' : undefined;
  const suffix =
    field.kind === 'percent'
      ? '%'
      : field.kind === 'months'
        ? 'mo'
        : field.kind === 'years'
          ? 'yr'
          : undefined;

  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {field.label}
      </label>
      <div className="flex h-11 items-center rounded-lg border border-ink-200 bg-white focus-within:border-transparent focus-within:ring-2 focus-within:ring-cheong-500 dark:border-surface-dark-border dark:bg-surface-dark-card">
        {prefix && (
          <span className="select-none pl-3 text-sm text-ink-400" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={String(value)}
          aria-describedby={describedBy}
          onChange={(e) => {
            const raw = e.target.value;
            // Allow an empty box and a lone "-" or trailing "." while typing.
            if (raw === '' || raw === '-' || raw.endsWith('.')) {
              onChange(raw);
              return;
            }
            const parsed = Number.parseFloat(raw.replace(/,/g, ''));
            onChange(Number.isFinite(parsed) ? parsed : raw);
          }}
          onBlur={(e) => {
            const parsed = Number.parseFloat(e.target.value.replace(/,/g, ''));
            let next = Number.isFinite(parsed) ? parsed : (field.defaultValue as number);
            if (field.min != null) next = Math.max(next, field.min);
            if (field.max != null) next = Math.min(next, field.max);
            onChange(next);
          }}
          className={`h-full w-full min-w-0 bg-transparent px-3 font-mono text-sm tabular-nums text-ink-900 outline-none dark:text-ink-50 ${
            prefix ? 'pl-1.5' : ''
          } ${suffix ? 'pr-1.5' : ''}`}
        />
        {suffix && (
          <span className="select-none pr-3 text-sm text-ink-400" aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
      {field.help && <FieldHelp id={describedBy!}>{field.help}</FieldHelp>}
    </div>
  );
}

function FieldHelp({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} className="mt-1.5 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
      {children}
    </p>
  );
}
